import React, { FC, useState, Fragment, ReactNode, useEffect } from 'react';
import styled from 'styled-components';
import { GenericListItem } from './GenericListItem';
import { Colors } from '../../styles/colors';
import { Icon } from '../general/Icon';
import { GenericListColumn, SortByProps } from './GenericList';
import { FlexboxVertical } from '../general/Layout';
import { AccordionSection } from '../general/AccordionSection';

const Wrapper = styled.div`
  display: grid;
  grid-template-rows: minmax(2rem, auto);
  background-color: ${Colors.white};
  padding: .75rem 1.5rem;
  margin-bottom: 1rem;
  cursor: pointer;

  & > div:first-child {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    width: 100%;
    padding: 0 .5rem 0 0;
    display: inline-block;
    line-height: 2;
  }
`;

const CollapseTd = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const CollapseCellContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  
  @media (pointer: fine) {
    &:hover {
      color: ${Colors.tertiary};
    }  
  }
`;

const ContentSpacer = styled.div<{ visible?: boolean }>`
  transition: height 150ms ease-out;
  height: ${({ visible = false }) => visible ? '1rem' : '0'};
`;

const AdditionalContentRow = styled.div`
  display: flex;
  margin-top: 2rem;
`;

export interface GenericTableBodyProps {
  items: any[];
  columns: GenericListColumn[];
  collapsible: boolean;
  additionalRenderFn?: (item: any, collapsed: boolean) => ReactNode;
  additionalContentConfig?: { columnTemplate: string };
  sortBy: SortByProps;
  pagination?: {
    page?: number,
    itemsPerPage?: number,
  },
  firstItemOpen?: boolean;
}

export const GenericListBody: FC<GenericTableBodyProps> = (props) => {
  const { columns, items, collapsible, additionalRenderFn, sortBy, pagination, additionalContentConfig, firstItemOpen = false } = props;
  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  const columnNames = columns.map((column) => column.name);

  useEffect(() => {
    if (firstItemOpen) {
      setExpandedRows([0]);
    }
  }, []);

  const compare = (asc: boolean = true) => (a: any, b: any) => {
    try {
      const valueA = a[sortBy.column];
      const valueB = b[sortBy.column];

      const compA = asc ? valueA : valueB;
      const compB = asc ? valueB : valueA;

      const stringCompare = typeof valueA === 'string' && typeof valueB === 'string';

      if (stringCompare) {
        return compA.localeCompare(compB);
      }
      return a - b;
    } catch (err) {
      //
    }
    return 0;
  };

  const page = pagination?.page || 0;
  const itemsPerPage = pagination?.itemsPerPage || 10;

  const sortedItems = items?.slice()
    .sort(compare(sortBy.asc))
    .filter((item, index) => (index >= page * itemsPerPage && index < page * itemsPerPage + itemsPerPage));

  return (
    <Fragment>
      {
        sortedItems?.map((item, index) => (
          <Wrapper
            key={item.id}
            style={{ gridTemplateColumns: `${columns.map((col) => (col.widthTemplate || '1fr')).join(' ')} ${collapsible ? '1.5rem' : ''}` }}
            onClick={() => {
              setExpandedRows([...expandedRows, index].filter((value) => {
                return !(value === index && expandedRows.includes(index));
              }));
            }}
          >
            {
              columnNames.map((key, columnIndex) => (
                <GenericListItem
                  item={item[key]}
                  key={key}
                  formatFn={columns[columnIndex]?.formatFn && ((formatItem) => columns[columnIndex]?.formatFn(formatItem, !expandedRows.includes(index), item))} />
              ))
            }
            {
              collapsible &&
              <CollapseTd>
                <CollapseCellContainer>
                  {
                    expandedRows.includes(index) ?
                      <Icon icon={'minus'} size={20} color={'inherit'} />
                      :
                      <Icon icon={'plus'} size={20} color={'inherit'} />
                  }
                </CollapseCellContainer>
              </CollapseTd>
            }
            {
              additionalRenderFn &&
              <AccordionSection
                collapsed={!expandedRows.includes(index)}
                style={{
                  cursor: 'auto',
                  gridColumn: additionalContentConfig?.columnTemplate || `span ${columns.length}`,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
              >
                { additionalRenderFn && additionalRenderFn(item, !expandedRows.includes(index)) }
              </AccordionSection>
            }
            {
              additionalRenderFn &&
              <ContentSpacer visible={expandedRows.includes(index)} />
            }
          </Wrapper>
        ))
      }
    </Fragment>
  );
};
